/*
 * Text-fit estimation (pure — unit-tested). Placeholder geometry is a hard
 * budget: agents kept cramming paragraph-length text into fixed template
 * placeholders, which PowerPoint autofit either shrinks to illegibility or
 * lets bleed off the slide. These heuristics turn a write into a measurable
 * fits / doesn't-fit signal.
 *
 * Model: average glyph width ≈ 0.55 × font size; line height ≈ 1.35 × font
 * size. Coarse, but errs conservative enough to catch the 3× overflows that
 * ruin slides while ignoring near-fits.
 */

export interface FitEstimate {
  fits: boolean;
  linesNeeded: number;
  linesAvailable: number;
  approxCharsFit: number;
}

export function estimateTextFit(text: string, wPt: number, hPt: number, fontPt: number): FitEstimate {
  const size = fontPt > 0 ? fontPt : 18;
  const charsPerLine = Math.max(8, Math.floor(wPt / (0.55 * size)));
  const lineH = size * 1.35;
  const linesAvailable = Math.max(1, Math.floor(hPt / lineH));
  let linesNeeded = 0;
  for (const para of String(text ?? "").split(/\r?\n/)) {
    linesNeeded += Math.max(1, Math.ceil(para.length / charsPerLine));
  }
  return {
    fits: linesNeeded <= linesAvailable,
    linesNeeded,
    linesAvailable,
    approxCharsFit: charsPerLine * linesAvailable,
  };
}

/** Human-readable overflow warning for a skipped[] entry, or null if it fits. */
export function overflowWarning(text: string, wPt: number, hPt: number, fontPt: number): string | null {
  const est = estimateTextFit(text, wPt, hPt, fontPt);
  if (est.fits) return null;
  return (
    `WARNING (text written, but likely OVERFLOWS the ${Math.round(wPt)}x${Math.round(hPt)}pt box: ` +
    `needs ~${est.linesNeeded} lines at ${Math.round(fontPt)}pt, box fits ~${est.linesAvailable}). ` +
    `Shorten to <=${est.approxCharsFit} chars, set a smaller explicit font size, or restructure ` +
    `(table / second slide / different layout).`
  );
}
